import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import axios from 'axios'

export interface RoommateProfile {
  id: string
  userId: string
  lookingFor: 'room' | 'roommate' | 'both'
  housingType: string[]
  budgetRange: { min: number; max: number; currency: string }
  preferredLocation: {
    cities: string[]
    maxCommuteTime?: number
    transportationMode?: 'car' | 'public_transit' | 'walking' | 'cycling'
  }
  lifestyle: {
    smokingTolerance: 'smoker' | 'no_smoking' | 'occasional' | 'tolerant'
    drinkingHabits: 'non_drinker' | 'occasional' | 'social' | 'regular'
    petTolerance: 'loves_pets' | 'okay_with_pets' | 'allergic' | 'no_pets'
    cleanlinessLevel: 'very_clean' | 'clean' | 'average' | 'relaxed'
    noiseLevel: 'very_quiet' | 'quiet' | 'moderate' | 'lively'
    guestPolicy: 'frequent_guests' | 'occasional_guests' | 'rare_guests' | 'no_guests'
  }
  workSchedule: 'traditional' | 'night_shift' | 'flexible' | 'work_from_home'
  bedtime: 'early_bird' | 'night_owl' | 'flexible'
  kitchenUsage: 'frequent_cook' | 'occasional_cook' | 'rarely_cook' | 'takeout_only'
  sharedSpaceUsage: 'social' | 'moderate' | 'minimal'
  culturalPreferences: {
    preferEthiopianRoommate: boolean
    speaksAmharic: boolean
    speaksOromo: boolean
    speaksOtherEthiopianLanguages?: string[]
    practicesEthiopianTraditions: boolean
    dietaryRestrictions?: string[]
    religiousObservance?: 'orthodox' | 'muslim' | 'protestant' | 'other' | 'none'
  }
  moveInDate: string
  leaseLength: 'month_to_month' | '6_months' | '1_year' | '2_years' | 'flexible'
  hasCurrentHousing: boolean
  urgencyLevel: 'not_urgent' | 'moderate' | 'urgent' | 'very_urgent'
  occupation: string
  ageRange: string
  hasReferences: boolean
  backgroundCheckConsent: boolean
  additionalInfo?: string
  profileCompletion: number
  verificationStatus: 'pending' | 'verified' | 'rejected'
  createdAt: string
  updatedAt: string
  status: 'active' | 'paused' | 'matched' | 'expired'
}

export interface RoommateMatch {
  id: string
  user1Id: string
  user2Id: string
  matchScore: number
  matchFactors: string[]
  status: 'suggested' | 'mutual_interest' | 'contacted' | 'rejected'
  createdAt: string
  lastInteractionAt?: string
}

export interface RoommateFilters {
  budgetMin?: number
  budgetMax?: number
  location?: string
  moveInDate?: string
  leaseLength?: string[]
  lifestyleSmoking?: string
  lifestylePets?: string
  lifestyleCleanliness?: string[]
  culturalEthiopianPreferred?: boolean
  culturalLanguages?: string[]
  culturalReligious?: string[]
  ageRange?: string
  occupation?: string[]
}

interface RoommateState {
  profile: RoommateProfile | null
  matches: RoommateMatch[]
  suggestedMatches: RoommateProfile[]
  mutualInterests: RoommateMatch[]
  interestedInMe: RoommateMatch[]
  searchResults: RoommateProfile[]
  loading: boolean
  matchesLoading: boolean
  error: string | null
  filters: RoommateFilters
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

const initialState: RoommateState = {
  profile: null,
  matches: [],
  suggestedMatches: [],
  mutualInterests: [],
  interestedInMe: [],
  searchResults: [],
  loading: false,
  matchesLoading: false,
  error: null,
  filters: {},
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
}

const API_BASE = '/api/backend'
const ROOMMATE_FEATURE_ENABLED = process.env.NEXT_PUBLIC_ENABLE_ROOMMATES === 'true'

// Async thunks
export const fetchMyRoommateProfile = createAsyncThunk(
  'roommate/fetchMyProfile',
  async (_, { rejectWithValue }) => {
    if (!ROOMMATE_FEATURE_ENABLED) {
      return rejectWithValue('Roommate features are disabled')
    }
    try {
      const response = await axios.get(`${API_BASE}/roommates/me`)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch profile')
    }
  }
)

export const createRoommateProfile = createAsyncThunk(
  'roommate/createProfile',
  async (profileData: Omit<RoommateProfile, 'id' | 'createdAt' | 'updatedAt'>, { rejectWithValue }) => {
    if (!ROOMMATE_FEATURE_ENABLED) {
      return rejectWithValue('Roommate features are disabled')
    }
    try {
      const response = await axios.post(`${API_BASE}/roommates/me`, profileData)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create profile')
    }
  }
)

export const updateRoommateProfile = createAsyncThunk(
  'roommate/updateProfile',
  async (profileData: Partial<RoommateProfile>, { rejectWithValue }) => {
    if (!ROOMMATE_FEATURE_ENABLED) {
      return rejectWithValue('Roommate features are disabled')
    }
    try {
      const response = await axios.put(`${API_BASE}/roommates/me`, profileData)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update profile')
    }
  }
)

export const fetchMatches = createAsyncThunk(
  'roommate/fetchMatches',
  async (_, { rejectWithValue }) => {
    if (!ROOMMATE_FEATURE_ENABLED) {
      return rejectWithValue('Roommate features are disabled')
    }
    try {
      const response = await axios.get(`${API_BASE}/roommates/matches`)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch matches')
    }
  }
)

export const searchRoommates = createAsyncThunk(
  'roommate/searchRoommates',
  async ({ filters = {} }: { filters?: RoommateFilters }, { rejectWithValue }) => {
    if (!ROOMMATE_FEATURE_ENABLED) {
      return rejectWithValue('Roommate features are disabled')
    }
    try {
      const params = new URLSearchParams(
        Object.entries(filters).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = Array.isArray(value) ? value.join(',') : value.toString()
          }
          return acc
        }, {} as Record<string, string>)
      )

      const response = await axios.get(`${API_BASE}/roommates/search?${params}`)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Search failed')
    }
  }
)

export const expressInterest = createAsyncThunk(
  'roommate/expressInterest',
  async (roommateId: string, { rejectWithValue }) => {
    if (!ROOMMATE_FEATURE_ENABLED) {
      return rejectWithValue('Roommate features are disabled')
    }
    try {
      const response = await axios.post(`${API_BASE}/roommates/${roommateId}/interest`)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to express interest')
    }
  }
)

export const withdrawInterest = createAsyncThunk(
  'roommate/withdrawInterest',
  async (roommateId: string, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_BASE}/roommates/${roommateId}/interest`)
      return roommateId
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to withdraw interest')
    }
  }
)

export const fetchMutualInterests = createAsyncThunk(
  'roommate/fetchMutualInterests',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE}/roommates/mutual`)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch mutual interests')
    }
  }
)

export const fetchInterestedInMe = createAsyncThunk(
  'roommate/fetchInterestedInMe',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_BASE}/roommates/interested`)
      return response.data.data
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch interested users')
    }
  }
)

const roommateSlice = createSlice({
  name: 'roommate',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setFilters: (state, action: PayloadAction<RoommateFilters>) => {
      state.filters = action.payload
    },
    clearFilters: (state) => {
      state.filters = {}
    },
    clearSearchResults: (state) => {
      state.searchResults = []
    },
    updateProfileCompletion: (state, action: PayloadAction<number>) => {
      if (state.profile) {
        state.profile.profileCompletion = action.payload
      }
    },
  },
  extraReducers: (builder) => {
    // fetchMyRoommateProfile
    builder
      .addCase(fetchMyRoommateProfile.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchMyRoommateProfile.fulfilled, (state, action) => {
        state.loading = false
        state.profile = action.payload
      })
      .addCase(fetchMyRoommateProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // createRoommateProfile
    builder
      .addCase(createRoommateProfile.pending, (state) => {
        state.loading = true
      })
      .addCase(createRoommateProfile.fulfilled, (state, action) => {
        state.loading = false
        state.profile = action.payload
      })
      .addCase(createRoommateProfile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // updateRoommateProfile
    builder
      .addCase(updateRoommateProfile.fulfilled, (state, action) => {
        state.profile = action.payload
      })

    // fetchMatches
    builder
      .addCase(fetchMatches.pending, (state) => {
        state.matchesLoading = true
      })
      .addCase(fetchMatches.fulfilled, (state, action) => {
        state.matchesLoading = false
        state.suggestedMatches = action.payload
      })
      .addCase(fetchMatches.rejected, (state, action) => {
        state.matchesLoading = false
        state.error = action.payload as string
      })

    // searchRoommates
    builder
      .addCase(searchRoommates.pending, (state) => {
        state.loading = true
      })
      .addCase(searchRoommates.fulfilled, (state, action) => {
        state.loading = false
        state.searchResults = action.payload
      })
      .addCase(searchRoommates.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // expressInterest
    builder
      .addCase(expressInterest.fulfilled, (state, action) => {
        state.matches.push(action.payload)
      })

    // fetchMutualInterests
    builder
      .addCase(fetchMutualInterests.fulfilled, (state, action) => {
        state.mutualInterests = action.payload
      })

    // fetchInterestedInMe
    builder
      .addCase(fetchInterestedInMe.fulfilled, (state, action) => {
        state.interestedInMe = action.payload
      })
  },
})

export const { 
  clearError, 
  setFilters, 
  clearFilters, 
  clearSearchResults, 
  updateProfileCompletion 
} = roommateSlice.actions

export default roommateSlice.reducer
